import { useMemo } from 'react';

import { useDeepEqualMemoValue } from '@components/component-helpers';
import { IconAssigned } from '@components/icons/assigned';
import { FilterConfigField } from '@components/table';

import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';
import { IUser } from 'models/types';

export const useFilterAssignedTo = (): FilterConfigField => {
  const { t, userStore } = useStore();
  const me = useAuthenticatedUser();

  const users = useDeepEqualMemoValue(
    userStore.list.map((user: IUser) => ({
      value: String(user.id),
      label: user.name,
    }))
  );

  return useMemo(
    () => ({
      id: 'assignedto',
      label: t('messages.filters.assigned.title'),
      icon: <IconAssigned />,
      type: 'user',
      values: (filter) => {
        if (filter.comparer !== 'is') {
          return [];
        }

        return [
          { value: 'none', label: t('messages.filters.assigned.none') },
          { value: 'anyone', label: t('messages.filters.assigned.anyone') },
          ...users,
        ];
      },
      quick: [
        {
          id: 'me',
          label: t('messages.filters.assigned.me'),
          apply: { comparer: 'is', value: String(me.id) },
        },
        {
          id: 'none',
          label: t('messages.filters.assigned.none'),
          apply: { comparer: 'is', value: 'none' },
        },
        {
          id: 'anyone',
          label: t('messages.filters.assigned.anyone'),
          apply: { comparer: 'is', value: 'anyone' },
        },
      ],
      allowedComparers: ['is'],
      toExternalQuery: (filter) => {
        switch (filter.comparer) {
          case 'is':
            if (filter.value === 'anyone') return { application__assigned_to_id__isnull: false };
            if (filter.value === 'none') return { application__assigned_to_id__isnull: true };
            return { application__assigned_to_id: filter.value };
          default:
            return {};
        }
      },
    }),
    [me.id, t, users]
  );
};
